/**
 * 견적서 상태 매핑 단일 소스
 * DB 스키마 → UI 표기 고정
 */

export type EstimateStatus = 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'DRAFT' | 'WITHDRAWN'
export type NormalizedStatus = 'pending' | 'accept' | 'rejected'

/**
 * DB 상태값을 UI 표준 상태값으로 변환
 */
export function normalizeStatus(status: string | null | undefined): NormalizedStatus {
  if (!status) return 'pending'
  
  const upperStatus = status.toUpperCase()
  
  switch (upperStatus) {
    case 'SUBMITTED':
    case 'DRAFT':
      return 'pending'
    case 'ACCEPTED':
      return 'accept'
    case 'REJECTED':
    case 'WITHDRAWN':
      return 'rejected'
    default:
      // 레거시 소문자 상태값 하위 호환
      const lowerStatus = status.toLowerCase()
      if (lowerStatus === 'pending' || lowerStatus === 'accept' || lowerStatus === 'rejected') {
        return lowerStatus as NormalizedStatus
      }
      return 'pending'
  }
}

/**
 * 상태 라벨 (한국어) 고정
 */
export function getStatusLabel(status: string | null | undefined): string {
  const normalized = normalizeStatus(status)
  
  switch (normalized) {
    case 'pending':
      return '검토중'
    case 'accept':
      return '수락됨'
    case 'rejected':
      return '거절됨'
    default:
      return '검토중'
  }
}

/**
 * 상태 배지 색상 클래스
 */
export function getStatusBadgeClass(status: string | null | undefined): string {
  const normalized = normalizeStatus(status)
  
  switch (normalized) {
    case 'pending':
      return 'bg-blue-50 text-blue-700'
    case 'accept':
      return 'bg-blue-100 text-blue-800'
    case 'rejected':
      return 'bg-gray-100 text-gray-700'
    default:
      return 'bg-blue-50 text-blue-700'
  }
}

/**
 * 상태 비교 헬퍼
 */
export function isStatus(
  estimateStatus: string | null | undefined,
  targetStatus: NormalizedStatus
): boolean {
  return normalizeStatus(estimateStatus) === targetStatus
}

/**
 * UI 상태 타입 (새로운 타입 정의)
 */
export type DbEstimateStatus = 'DRAFT' | 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'WITHDRAWN'
export type UiEstimateStatus = 'pending' | 'accept' | 'rejected'

/**
 * DB 상태를 UI 상태로 변환 (타입 안전 버전)
 */
export function toUiStatus(dbStatus: DbEstimateStatus | string | null | undefined): UiEstimateStatus | null {
  if (!dbStatus) return null
  
  const upperStatus = dbStatus.toUpperCase()
  
  switch (upperStatus) {
    case 'SUBMITTED':
    case 'DRAFT':
      return 'pending'
    case 'ACCEPTED':
      return 'accept'
    case 'REJECTED':
    case 'WITHDRAWN':
      return 'rejected'
    default:
      return null // 고객 UI 기본 노출 제외
  }
}

/**
 * UI 상태를 라벨로 변환
 */
export function toStatusLabel(uiStatus: UiEstimateStatus): string {
  switch (uiStatus) {
    case 'pending':
      return '검토중'
    case 'accept':
      return '수락됨'
    case 'rejected':
      return '거절됨'
  }
}

/**
 * UI 상태를 Badge variant로 변환
 */
export function toStatusTone(uiStatus: UiEstimateStatus): 'default' | 'secondary' | 'destructive' {
  switch (uiStatus) {
    case 'pending':
      return 'secondary'
    case 'accept':
      return 'default'
    case 'rejected':
      return 'destructive'
  }
}

